import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import ViewMode from '@/shared/enums/ViewMode';
import { useScenario } from '@/shared/hooks';
import useComparison from '@/components/DesignContainer/hooks/useComparison';
import formatScenario from '../utils/scenarios/formatScenario';

export const ScenarioContext = createContext({
  access: null,
  canPublish: null,
  publish: null,
  scenario: null,
  selectScenario: null,
  snapshotEntityMap: null,
});

export const useScenarioContext = () => useContext(ScenarioContext);

const ScenarioProvider = ({
  children,
  comparisonMeta,
  designId,
  existingScenario,
  level,
  optimisticSnapshot,
  includeInactive,
  storeType,
  view,
}) => {
  const [includeNested, setIncludeNested] = useState(true);
  const [allowChanges, setAllowChanges] = useState(view !== ViewMode.DIAGRAM);

  const {
    access,
    canPublish,
    create,
    publish,
    scenario,
    selectScenario,
    snapshotEntityMap,
  } = useScenario({
    allowChanges,
    designId,
    existingScenario,
    includeInactive,
    includeNested,
    level,
    optimisticSnapshot,
    storeType,
  });

  const { activeComparison, loadComparison, cancelComparison } =
    useComparison(comparisonMeta);

  const comparisonScenario = useMemo(() => {
    const isHistorical = activeComparison?.revision.is_historical ?? false;
    return activeComparison?.design
      ? formatScenario({
          ...activeComparison,
          includeNested,
          isHistorical,
        })
      : null;
  }, [activeComparison, includeNested]);

  const toggleAllowChanges = useCallback(
    (value) => {
      // Always allow changes outside of the diagram view.
      if (view !== ViewMode.DIAGRAM) {
        setAllowChanges(true);
        return;
      }
      setAllowChanges(value);
    },
    [view]
  );

  useEffect(() => {
    toggleAllowChanges(view !== ViewMode.DIAGRAM);
  }, [view]);

  const contextState = useMemo(() => {
    return {
      access,
      canPublish,
      create,
      publish,
      scenario,
      selectScenario,
      snapshotEntityMap,
      toggleAllowChanges,
      includeNested,
      setIncludeNested,
      comparisonScenario,
      // would it be worth including the snapshotEntityMap in the formatScenario output?
      comparisonScenarioEntityMap: activeComparison?.snapshotEntityMap,
      loadComparison,
      cancelComparison,
    };
  }, [
    access,
    canPublish,
    includeNested,
    scenario,
    snapshotEntityMap,
    comparisonScenario,
    activeComparison,
    toggleAllowChanges,
  ]);

  return (
    <ScenarioContext.Provider value={contextState}>
      {children}
    </ScenarioContext.Provider>
  );
};

export default ScenarioProvider;
